import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from "react-router-dom";
import './Login.css';
import apiService from '../../services/ApiServices';

function LoginPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [formData, setFormData] = useState({
    username: '',    // we're using this field for both username OR email
    password: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when user starts typing
    if (loginError) setLoginError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.username.trim() || !formData.password.trim()) {
      setLoginError('Please enter both username/email and password');
      return;
    }

    try {
      setIsLoading(true);
      setLoginError('');

      const response = await apiService.post('/api/user-admin/login', {
        userNameOrEmail: formData.username.trim(),
        password: formData.password
      });

      // New response structure from your backend
      const { success, message } = response;

      // Handle when message is an empty object or missing token/user
      if (!success) {
        // Check if message exists and has content
        if (message && typeof message === 'object') {
          if (message.token && message.user) {
            // This shouldn't happen if success is false, but just in case
            const { token, user } = message;
            localStorage.setItem('user', JSON.stringify(user));
            localStorage.setItem('AUTH_TOKEN_KEY', token);
            setTimeout(() => {
              navigate('/dashboard');
            }, 400);
            return;
          } else if (message.error || message.message) {
            // If backend sends error message in message field
            throw new Error(message.error || message.message);
          } else {
            // Empty object case
            throw new Error('Invalid username/email or password');
          }
        } else {
          // message is not an object or is undefined
          throw new Error('Invalid credentials. Please try again.');
        }
      }

      // If success is true, proceed normally
      if (!message?.token || !message?.user) {
        throw new Error('Invalid response from server');
      }

      const { token, user } = message;

      // Store user data and token
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('AUTH_TOKEN_KEY', token);

      // Small delay for better UX (optional)
      setTimeout(() => {
        navigate('/dashboard');
      }, 400);

    } catch (error) {
      console.error('Login failed:', error);

      // Better error handling
      let errorMessage = 'Login failed. Please check your credentials and try again.';

      if (error.response?.data) {
        const { success, message } = error.response.data;

        if (!success) {
          if (message && typeof message === 'object') {
            if (message.error || message.message) {
              errorMessage = message.error || message.message;
            } else {
              errorMessage = 'Invalid username/email or password';
            }
          } else if (typeof message === 'string') {
            errorMessage = message;
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setLoginError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const authToken = localStorage.getItem("AUTH_TOKEN_KEY");

    if (storedUser && authToken) {
      navigate('/dashboard');
    }
  }, [navigate]);

  return (
    <div className="login-container min-vh-100 d-flex align-items-center justify-content-center py-4 px-3">
      {/* Decorative floating blurred background shapes */}
      <div className="bg-shape-1"></div>
      <div className="bg-shape-2"></div>
      <div className="bg-shape-3"></div>

      <div className="login-card-wrapper w-100">
        <div className="card shadow-lg border-0 rounded-4 login-card">
          <div className="card-body p-4 p-sm-5 position-relative">

            {/* Loading Overlay */}
            {isLoading && (
              <div className="loading-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-light bg-opacity-75 rounded-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <span className="ms-2">Signing in...</span>
              </div>
            )}

            {/* Header */}
            <div className="text-center mb-4">
              <div className="logo-container rounded-circle d-inline-flex align-items-center justify-content-center mb-3">
                <img
                  src="/logo512.png"
                  alt="Logo"
                  className="logo-img"
                />
              </div>
              <h2 className="fw-bold text-dark mb-1">Welcome Back</h2>
              <p className="text-muted">Please sign in to your account</p>
            </div>

            {/* Error Message */}
            {loginError && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                <i className="fas fa-exclamation-circle me-2"></i>
                {loginError}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setLoginError('')}
                  aria-label="Close"
                ></button>
              </div>
            )}

             {/* Login Form */}
             <form onSubmit={handleLogin}>
               <div className="mb-3">
                 <label htmlFor="username" className="form-label fw-semibold">
                   <i className="fas fa-user me-2 text-primary"></i>
                   Username or Email
                 </label>
                 <input
                   type="text"
                   className="form-control py-2"
                   id="username"
                   name="username"
                   value={formData.username}
                   onChange={handleInputChange}
                   placeholder="Enter your username or email"
                   disabled={isLoading}
                   required
                 />
               </div>

               <div className="mb-4">
                 <label htmlFor="password" className="form-label fw-semibold">
                   <i className="fas fa-key me-2 text-primary"></i>
                   Password
                 </label>
                 <div className="login-v2-password-wrap position-relative">
                   <input
                     type={showPassword ? "text" : "password"}
                     className="form-control py-2 login-v2-password-input"
                     id="password"
                     name="password"
                     value={formData.password}
                     onChange={handleInputChange}
                     placeholder="Enter your password"
                     disabled={isLoading}
                     required
                   />
                   <button
                     className="login-v2-visibility"
                     type="button"
                     onClick={togglePasswordVisibility}
                     disabled={isLoading}
                   >
                     <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                   </button>
                 </div>
                 <div className="mt-2 d-flex justify-content-end">
                   <Link to="/forgot-password" className="text-decoration-none small text-primary">
                     <i className="fas fa-question-circle me-1"></i>
                     Forgot Password?
                   </Link>
                 </div>
               </div>

              <div className="d-grid mb-3">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg py-2 fw-semibold"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Signing In...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-sign-in-alt me-2"></i>
                      Sign In
                    </>
                  )}
                </button>
              </div>
            </form>

            {/* Footer Info */}
            <div className="mt-4 pt-3 border-top text-center">
              <small className="text-muted">
                <i className="fas fa-lock me-1"></i>
                Your information is securely encrypted
              </small>
            </div>

          </div>
        </div>
      </div>
    </div>

  );
}

export default LoginPage;